import React, { useState, useEffect, useRef } from 'react';
import styles from './CategoryMatcherGame.module.css';
import plaidService, { Transaction } from '../services/plaidService';

interface GameTransaction {
  id: number;
  name: string;
  category: string;
}

function shuffleArray<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

interface Bubble {
  id: string;
  label: string;
  position: number;
}

const DROP_INTERVAL = 1500; // ms

const CategoryMatcherGame: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState(60);
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [started, setStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const dropRef = useRef<NodeJS.Timeout | null>(null);
  const gameStateRef = useRef({ started, gameOver, timeLeft });
  const [noTransactions, setNoTransactions] = useState(false);
  const [gameTransactions, setGameTransactions] = useState<GameTransaction[]>([]);
  const bubblePositions = useRef<Map<string, number>>(new Map());

  // Update game state ref whenever relevant state changes
  useEffect(() => {
    gameStateRef.current = { started, gameOver, timeLeft };
    console.log('Game state updated:', gameStateRef.current);
  }, [started, gameOver, timeLeft]);

  useEffect(() => {
    const loadTransactions = async () => {
      try {
        setLoading(true);
        const txs: Transaction[] = await plaidService.getPersistedTransactions();
        const allNames = txs.map(tx => tx.name).filter(Boolean);
        const uniqueNames = Array.from(new Set(allNames));
        const shuffled = shuffleArray(uniqueNames).map((name, index) => ({
          id: index,
          name,
          category: ''
        }));
        setGameTransactions(shuffled);
        setNoTransactions(shuffled.length === 0);
      } catch (err) {
        setError('Failed to load transactions');
        console.error('Error loading transactions:', err);
      } finally {
        setLoading(false);
      }
    };

    loadTransactions();
  }, []);

  const startGame = () => {
    if (gameTransactions.length === 0) {
      setNoTransactions(true);
      return;
    }
    setStarted(true);
    setGameOver(false);
    setTimeLeft(60);
    // Create bubbles from gameTransactions
    const newBubbles = gameTransactions.slice(0, 5).map((tx, index) => ({
      id: `bubble-${index}`,
      label: tx.name,
      position: Math.random() * (window.innerWidth - 100)
    }));
    setBubbles(newBubbles);
  };

  // Timer effect - only depends on started state
  useEffect(() => {
    if (!started) return;

    console.log('Setting up timer interval');
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev > 1) return prev - 1;
        setGameOver(true);
        return 0;
      });
    }, 1000);

    return () => {
      console.log('Cleaning up timer interval');
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [started]); // Only depend on started state

  // Drop interval effect - only depends on started and gameOver states
  useEffect(() => {
    if (!started || gameOver) return;

    console.log('Setting up drop interval');
    dropRef.current = setInterval(() => {
      setBubbles((prevBubbles) => {
        if (prevBubbles.length === 0) {
          console.log('Queue is empty, stopping drop interval');
          if (dropRef.current) {
            clearInterval(dropRef.current);
            dropRef.current = null;
          }
          return prevBubbles;
        }
        const [next, ...rest] = prevBubbles;
        console.log('Dropping bubble:', next, 'Remaining in queue:', rest.length);
        return [...rest, next];
      });
    }, DROP_INTERVAL);

    return () => {
      console.log('Cleaning up drop interval');
      if (dropRef.current) {
        clearInterval(dropRef.current);
        dropRef.current = null;
      }
    };
  }, [started, gameOver]); // Only depend on started and gameOver states

  // Clear everything when game is over
  useEffect(() => {
    if (gameOver) {
      console.log('Game over - cleaning up all intervals');
      if (dropRef.current) {
        clearInterval(dropRef.current);
        dropRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setBubbles([]);
    }
  }, [gameOver]);

  // Clean up intervals when component unmounts
  useEffect(() => {
    return () => {
      console.log('Component unmounting - cleaning up all intervals');
      if (dropRef.current) {
        clearInterval(dropRef.current);
        dropRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const handleBubbleClick = (id: string) => {
    setBubbles((prev) => prev.filter((b) => b.id !== id));
  };

  // Calculate random horizontal position for bubbles
  const getRandomX = () => {
    const maxX = 340 - 60; // container width - bubble width (reduced from 120 to 60)
    return Math.floor(Math.random() * maxX);
  };

  const getBubblePosition = (id: string) => {
    if (!bubblePositions.current.has(id)) {
      bubblePositions.current.set(id, getRandomX());
    }
    return bubblePositions.current.get(id);
  };

  if (loading) {
    return <div>Loading transactions...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (gameTransactions.length === 0) {
    return <div>No transactions available for the game.</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div>
          <h1 className={styles.title}>Category Matcher</h1>
          <div className={styles.subtitle}>Match transactions into their proper categories!</div>
        </div>
        <div className={styles.stats}>
          <div>Cost: <span className={styles.heart}>❤️</span> to play</div>
          <div><span className={styles.coin}>🪙</span> Coins: 0</div>
          <div><span className={styles.heart}>❤️</span> Lives: 3</div>
        </div>
      </div>
      <div className={styles.gameRow}>
        {started && !gameOver && <div className={styles.timer}>Time: {timeLeft}s</div>}
        <div className={styles.question}>What belongs in the <b>Needs</b> category?</div>
      </div>
      {!started && (
        <button className={styles.startButton} onClick={startGame} disabled={loading}>
          {loading ? 'Loading...' : 'Start'}
        </button>
      )}
      {error && <div className={styles.error}>{error}</div>}
      {noTransactions && <div className={styles.error}>No transactions found to display.</div>}
      {started && !gameOver && (
        <div className={styles.bubbleArea}>
          <div className={styles.optionsRow}>
            {bubbles.map((bubble, index) => (
              <div
                key={bubble.id}
                className={styles.option + ' ' + styles.falling}
                onClick={() => handleBubbleClick(bubble.id)}
                style={{
                  left: `${getBubblePosition(bubble.id)}px`,
                  animationDelay: `${index * 0.2}s`
                }}
              >
                {bubble.label}
              </div>
            ))}
            {bubbles.length === 0 && <div className={styles.error}>No bubbles on screen (debug).</div>}
          </div>
        </div>
      )}
      {gameOver && (
        <>
          <div className={styles.gameOverMsg}>Game Over</div>
          <button className={styles.restartButton} onClick={startGame}>
            Restart
          </button>
        </>
      )}
    </div>
  );
};

export default CategoryMatcherGame; 